import { useEffect, useRef, useState } from 'react';

// Clock picker custom (analog + digital), gaya Material time picker,
// tapi warnanya ikut var(--accent) punya web (bukan ungu kayak referensi).
// Dipakai buat field "Jam Acara" di form kontak/booking, gantiin
// <input type="time"> bawaan browser yang tampilannya beda-beda tiap OS.
//
// Animasi buka/tutup pakai keyframe `portfolio-modal-in/out` yang sudah ada
// (index.css) — spring bounce pas buka, spring-soft halus pas nutup — jadi
// bahasa gerak-nya konsisten sama modal portofolio yang lain di app ini.

function to12(hour24) {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  let h = hour24 % 12;
  if (h === 0) h = 12;
  return { hour12: h, period };
}
function to24(hour12, period) {
  let h = hour12 % 12;
  if (period === 'PM') h += 12;
  return h;
}
function pad2(n) {
  return String(n).padStart(2, '0');
}

const CENTER = 150;
const R_OUTER = 132;
const R_TICK = 124;
const R_NUM = 104;

function pointAt(value, steps, radius) {
  const angle = (value / steps) * Math.PI * 2 - Math.PI / 2;
  return { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
}

export default function TimePicker({ open, value, onClose, onSave }) {
  const [h24Init, mInit] = (value || '08:00').split(':').map((n) => parseInt(n, 10) || 0);
  const initial = to12(h24Init);
  const [hour12, setHour12] = useState(initial.hour12);
  const [minute, setMinute] = useState(mInit);
  const [period, setPeriod] = useState(initial.period);
  const [mode, setMode] = useState('hours'); // 'hours' | 'minutes'
  const [manual, setManual] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      // Reset ke waktu yang sedang tersimpan tiap kali dibuka lagi.
      const [h24, m0] = (value || '08:00').split(':').map((n) => parseInt(n, 10) || 0);
      const init = to12(h24);
      setHour12(init.hour12);
      setMinute(m0);
      setPeriod(init.period);
      setMode('hours');
      setManual(false);
      setClosing(false);
      setDisplayOpen(true);
    } else if (displayOpen) {
      setClosing(true);
      closeTimeoutRef.current = setTimeout(() => {
        setDisplayOpen(false);
        setClosing(false);
      }, 280);
    }
    return () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const dialRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromPointer = (clientX, clientY) => {
    const rect = dialRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let angle = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI + 90;
    if (angle < 0) angle += 360;
    if (mode === 'hours') {
      let h = Math.round(angle / 30) % 12;
      if (h === 0) h = 12;
      setHour12(h);
    } else {
      setMinute(Math.round(angle / 6) % 60);
    }
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    dialRef.current.setPointerCapture(e.pointerId);
    draggingRef.current = true;
    updateFromPointer(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) updateFromPointer(e.clientX, e.clientY);
  };
  const onPointerUp = () => {
    if (draggingRef.current && mode === 'hours') setMode('minutes');
    draggingRef.current = false;
  };

  const save = () => onSave(`${pad2(to24(hour12, period))}:${pad2(minute)}`);

  const steps = mode === 'hours' ? 12 : 60;
  const selectedValue = mode === 'hours' ? hour12 % 12 : minute;
  const handPoint = pointAt(selectedValue, steps, R_NUM);
  const marks = mode === 'hours' ? Array.from({ length: 12 }, (_, i) => i + 1) : Array.from({ length: 12 }, (_, i) => i * 5);

  const fieldBase = 'w-16 h-14 sm:w-20 sm:h-16 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl font-bold transition-all duration-200 bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-white';
  const fieldActive = { backgroundColor: 'color-mix(in oklab, var(--accent) 16%, transparent)', color: 'var(--accent)', boxShadow: '0 0 0 2px var(--accent)' };

  if (!displayOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-opacity duration-[280ms] ${closing ? 'opacity-0' : 'opacity-100'}`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-[380px] rounded-3xl bg-white dark:bg-neutral-800 shadow-2xl overflow-hidden ${closing ? 'portfolio-modal-out' : 'portfolio-modal-in'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">Masukkan Jam</p>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { setMode('hours'); setManual(false); }}
              className={fieldBase}
              style={mode === 'hours' && !manual ? fieldActive : undefined}
            >
              {pad2(hour12)}
            </button>
            <span className="text-3xl font-bold text-neutral-400 pb-1">:</span>
            <button
              type="button"
              onClick={() => { setMode('minutes'); setManual(false); }}
              className={fieldBase}
              style={mode === 'minutes' && !manual ? fieldActive : undefined}
            >
              {pad2(minute)}
            </button>

            <div className="ml-1 flex flex-col rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-600 h-14 sm:h-16 w-12 sm:w-14 text-xs font-bold flex-shrink-0">
              <button
                type="button"
                onClick={() => setPeriod('AM')}
                className="flex-1 flex items-center justify-center transition-colors text-neutral-500 dark:text-neutral-300"
                style={period === 'AM' ? { backgroundColor: 'color-mix(in oklab, var(--accent) 18%, transparent)', color: 'var(--accent)' } : undefined}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => setPeriod('PM')}
                className="flex-1 flex items-center justify-center transition-colors border-t border-neutral-200 dark:border-neutral-600 text-neutral-500 dark:text-neutral-300"
                style={period === 'PM' ? { backgroundColor: 'color-mix(in oklab, var(--accent) 18%, transparent)', color: 'var(--accent)' } : undefined}
              >
                PM
              </button>
            </div>
          </div>
        </div>

        {!manual ? (
          <div className="px-6 pb-2 pt-4 flex justify-center">
            <svg
              ref={dialRef}
              viewBox="0 0 300 300"
              className="w-[260px] h-[260px] sm:w-[280px] sm:h-[280px] touch-none select-none cursor-pointer"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              <circle cx={CENTER} cy={CENTER} r={R_OUTER} fill="color-mix(in oklab, var(--accent) 9%, transparent)" />

              {mode === 'minutes' &&
                Array.from({ length: 60 }, (_, i) => {
                  if (i % 5 === 0) return null;
                  const p = pointAt(i, 60, R_TICK);
                  return <circle key={i} cx={p.x} cy={p.y} r={1.8} fill="#9ca3af" opacity={0.6} />;
                })}

              <line x1={CENTER} y1={CENTER} x2={handPoint.x} y2={handPoint.y} stroke="var(--accent)" strokeWidth={2} />
              <circle cx={CENTER} cy={CENTER} r={4} fill="var(--accent)" />

              {marks.map((mVal) => {
                const stepVal = mode === 'hours' ? mVal % 12 : mVal;
                const p = pointAt(stepVal, steps, R_NUM);
                return (
                  <text
                    key={mVal}
                    x={p.x}
                    y={p.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ fontSize: 22, fontWeight: 700, fill: '#737373' }}
                  >
                    {mode === 'hours' ? mVal : pad2(mVal)}
                  </text>
                );
              })}

              <circle cx={handPoint.x} cy={handPoint.y} r={22} fill="var(--accent)" />
              <text
                x={handPoint.x}
                y={handPoint.y}
                textAnchor="middle"
                dominantBaseline="central"
                style={{ fontSize: 20, fontWeight: 700, fill: '#fff' }}
              >
                {mode === 'hours' ? hour12 : pad2(minute)}
              </text>
            </svg>
          </div>
        ) : (
          <div className="px-6 py-12 flex items-center justify-center gap-3">
            <input
              type="number"
              min="1"
              max="12"
              value={hour12}
              onChange={(e) => setHour12(Math.min(12, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              className="w-20 h-16 rounded-2xl text-center text-3xl font-bold border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-900/50 dark:text-white outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
            <span className="text-3xl font-bold text-neutral-400">:</span>
            <input
              type="number"
              min="0"
              max="59"
              value={minute}
              onChange={(e) => setMinute(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
              className="w-20 h-16 rounded-2xl text-center text-3xl font-bold border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-900/50 dark:text-white outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-4">
          <button
            type="button"
            onClick={() => setManual((m) => !m)}
            title={manual ? 'Kembali ke jam analog' : 'Ketik manual'}
            className="w-11 h-11 rounded-full flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            <span className="material-symbols-outlined text-xl">{manual ? 'schedule' : 'keyboard'}</span>
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-extrabold hover:bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] transition-colors"
              style={{ color: 'var(--accent)' }}
            >
              Batal
            </button>
            <button
              type="button"
              onClick={save}
              className="px-5 py-2.5 rounded-[20px] text-sm font-extrabold text-white shadow-sm hover:shadow-md hover:rounded-[14px] active:rounded-[10px] active:scale-95 transition-all duration-300 ease-[var(--ease-spring)]"
              style={{ backgroundColor: '#4A7A3A' }}
            >
              Simpan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
